import React, { useEffect, useState } from 'react';

import { chakra } from '@chakra-ui/system';
import { Tooltip } from '@chakra-ui/tooltip';

import type {
  AuthorizationState,
  Team,
} from 'components/common/Authorization/types';
import { useAuthorizationLogic } from 'components/common/Authorization/useAuthorizationLogic';
import ErrorBoundary from 'components/common/ErrorBoundary';

type AuthorizedComponentV2Props = {
  children: React.ReactNode;
  requiredPermission: string;
  team?: Team | null;
  signalsResource?: boolean;
  hideChildrenOnUnauthorized?: boolean;
  width?: number | string | null;
  teamPermission?: string;
  allowEither?: boolean;
  isLoading?: boolean;
};

/**
 * Authorization component that conditionally renders children based on user permissions.
 *
 * This component checks if the user has the required permission and either:
 * - Renders children normally if authorized
 * - Hides children completely if unauthorized and hideChildrenOnUnauthorized is true
 * - Disables children (makes them non-interactive) if unauthorized and hideChildrenOnUnauthorized is false
 * - Disables children and sets isLoading property when isLoading is true
 *
 * When children are disabled, they become non-clickable and show a tooltip explaining
 * the required permission.
 *
 * @param {React.ReactNode} children - The React elements to render conditionally
 * @param {string} requiredPermission - The permission string required to access this component
 * @param {Object} [team=null] - The team object for team-specific permission checks
 * @param {boolean} [signalsResource=false] - Whether this component signals a resource
 * @param {boolean} [hideChildrenOnUnauthorized=false] - Whether to hide children completely when unauthorized
 * @param {string} [width=null] - Optional width constraint for the wrapper element
 * @param {string} [teamPermission] - Optional specific team permission to check
 * @param {boolean} [allowEither=false] - Whether to allow access if either the main permission OR team permission is granted
 * @param {boolean} [isLoading=false] - Whether to hide children completely during loading state
 *
 * @example
 * ```jsx
 * <AuthorizedComponentV2
 *   requiredPermission="manage_teams"
 *   team={currentTeam}
 *   teamPermission="manage_users"
 * >
 *   <Button>Edit User</Button>
 * </AuthorizedComponentV2>
 * ```
 *
 * @example
 * ```jsx
 * <AuthorizedComponentV2
 *   requiredPermission="manage_teams"
 *   hideChildrenOnUnauthorized={true}
 * >
 *   <AdminPanel />
 * </AuthorizedComponentV2>
 * ```
 *
 * @example
 * ```jsx
 * <AuthorizedComponentV2
 *   requiredPermission="manage_teams"
 *   isLoading={isLoading}
 * >
 *   <Button>Save Changes</Button>
 * </AuthorizedComponentV2>
 * ```
 * When isLoading is true, the Button will be disabled and have isLoading={true} prop
 */
const AuthorizedComponentV2: React.FC<AuthorizedComponentV2Props> = ({
  children,
  requiredPermission,
  team = null,
  signalsResource = false,
  hideChildrenOnUnauthorized = false,
  width = null,
  teamPermission,
  allowEither = false,
  isLoading = false,
}) => {
  const authState: AuthorizationState = useAuthorizationLogic({
    requiredPermission,
    team,
    signalsResource,
    teamPermission,
    allowEither,
  });

  const [childElements, setChildElements] = useState<React.ReactNode>(children);

  const iterateChildren = (
    childArray: React.ReactNode,
    setIsLoading = false,
  ): React.ReactNode => {
    return React.Children.map(childArray, (child) => {
      if (React.isValidElement(child)) {
        const childProps = child.props as Record<string, unknown>;
        const updatedChild = React.cloneElement(child, {
          ...childProps,
          isDisabled: true,
          ...(setIsLoading && { isLoading: true }),
          style: {
            ...(childProps.style as Record<string, unknown>),
            pointerEvents: 'none',
            cursor: 'not-allowed',
          },
        } as any);
        if (childProps.children) {
          return React.cloneElement(updatedChild, {
            children: iterateChildren(
              childProps.children as React.ReactNode,
              setIsLoading,
            ),
          } as any);
        }
        return updatedChild;
      }
      return child;
    });
  };

  useEffect(() => {
    if (isLoading) {
      setChildElements(iterateChildren(children, true));
      return;
    }

    if (
      (!authState.isAuthorized && !authState.isLoading) ||
      authState.isError
    ) {
      if (hideChildrenOnUnauthorized) {
        setChildElements(null);
        return;
      }
      setChildElements(iterateChildren(children, false));
    }
  }, [
    authState.isAuthorized,
    authState.isLoading,
    authState.isError,
    children,
    hideChildrenOnUnauthorized,
    isLoading,
  ]);

  if (authState.isLoading) {
    return null;
  }

  if (isLoading) {
    return <>{childElements}</>;
  }

  if (authState.isAuthorized) {
    return <>{children}</>;
  }

  if (hideChildrenOnUnauthorized && !authState.isAuthorized) {
    return null;
  }

  const tooltipLabel = allowEither
    ? `Either the organization permission '${authState.orgPermissionDisplayName || requiredPermission}' or the team permission '${authState.teamPermissionDisplayName || teamPermission || requiredPermission}' is required to perform this action. Contact your organization or team administrator for support.`
    : `The permission '${authState.isTeamPermission ? authState.teamPermissionDisplayName : authState.orgPermissionDisplayName || requiredPermission}' is required to perform this action on the ${authState.isTeamPermission ? ` team '${team?.name}'` : 'organization'}. Contact your ${authState.isTeamPermission ? 'team' : 'organization'} administrator for support.`;

  return (
    <ErrorBoundary>
      <Tooltip hasArrow label={tooltipLabel}>
        <chakra.span width={width} tabIndex={0}>
          {childElements}
        </chakra.span>
      </Tooltip>
    </ErrorBoundary>
  );
};

export default AuthorizedComponentV2;
